import React, { useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PARKING_TYPES, EMPTY_CREATE_SLOT_FORM } from "@/lib/parkingTypes";

export default function CreateSlotDialog({ open, onOpenChange, onSaved }) {
    const [form, setForm] = useState(EMPTY_CREATE_SLOT_FORM);
    const [submitting, setSubmitting] = useState(false);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    async function handleSubmit(e) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.post("/parking/slots", {
                slot_number: form.slot_number.trim().toUpperCase(),
                type: form.type,
                category: form.parking_type,
                parking_type: form.parking_type,
                block: form.block || null,
                floor: form.floor || null,
                notes: form.notes || null,
                is_active: true,
            });
            toast.success("Parking slot created");
            setForm(EMPTY_CREATE_SLOT_FORM);
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Create parking slot</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Slot number *</Label>
                        <Input required data-testid="create-slot-number-input" placeholder="e.g., P-A-101, B1-4W-12"
                            value={form.slot_number} onChange={(e) => set("slot_number", e.target.value.toUpperCase())} className="rounded-sm border-brand-line uppercase font-mono" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Parking type *</Label>
                            <Select value={form.parking_type} onValueChange={(v) => set("parking_type", v)}>
                                <SelectTrigger data-testid="create-slot-parking-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{PARKING_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Sized for *</Label>
                            <Select value={form.type} onValueChange={(v) => set("type", v)}>
                                <SelectTrigger data-testid="create-slot-vehicle-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="2-wheeler">2-Wheeler</SelectItem>
                                    <SelectItem value="4-wheeler">4-Wheeler</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Block (optional)</Label>
                            <Input data-testid="create-slot-block-input" value={form.block} onChange={(e) => set("block", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2">
                            <Label>Floor (optional)</Label>
                            <Input data-testid="create-slot-floor-input" value={form.floor} onChange={(e) => set("floor", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Textarea data-testid="create-slot-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="bg-brand-sage/20 rounded-sm p-3">
                        <p className="text-xs text-brand-inkSoft">Slots start as <strong>Available</strong>. Use "Allocate" from the slot card to allot it to a flat and optionally enable paid parking charges.</p>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="create-slot-cancel-btn">Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="create-slot-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            {submitting ? "Creating…" : "Create slot"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
